import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import React from 'react'
import stylesheet from '../detail-container.stylesheet'
import { ClassNameMap, withStyles } from '@mui/styles'
import { checkCondition } from '../common/condition-check'
import DatePickerComponent from '../../../../utils/datepicker.component'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import Helper from '../../../../utils/helper'

interface IProps {
  nameError: boolean
  values: any
  handleInputChange: any
  validateInput: any
  classes: ClassNameMap
}

const PatientProfileDetailsComponent: React.FC<IProps> = ({
  nameError,
  values,
  handleInputChange,
  validateInput,
  classes,
}) => {
  const metaData = useSelector((state: RootState) => state.metaData.metadata)
  const prefix = ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr']
  const maritalStatus = ['Never Married', 'Married', 'Separated', 'Divorced', 'Windowed']
  const hospitals = (metaData?.HOSPITALS || [])

  return (
    <Box pl={5.5} pr={5} pb={1}>
      {!Helper.isAIDA() &&
        <Box>
          <Typography variant='subtitle2'>Device details</Typography>
          <Box pt={1} pb={1}>
            <Grid container justifyContent="space-between" spacing={2}>
              <Grid item xs={5.9}>
                <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
                  MAC address
                </Typography>
                <TextField
                  variant="standard"
                  fullWidth={true}
                  value={values.bpm_mac_address}
                  name="bpm_mac_address"
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={5.9}>
                <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
                  Refresh Token
                </Typography>
                <TextField
                  variant="standard"
                  fullWidth={true}
                  value={values.bpm_refresh_token}
                  name="bpm_refresh_token"
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={5.9}>
                <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>BP monitor</Typography>
                <TextField
                  variant="standard"
                  fullWidth={true}
                  value={values?.pylo_bpm_imei || ''}
                  name="pylo_bpm_imei"
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={5.9}>
                <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>Weighing scale</Typography>
                <TextField
                  variant="standard"
                  fullWidth={true}
                  value={values?.pylo_weighing_scale_imei}
                  name="pylo_weighing_scale_imei"
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={5.9}>
                <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
                  MBI id
                </Typography>
                <TextField
                  variant="standard"
                  fullWidth={true}
                  value={values.mbi_id}
                  name="mbi_id"
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ padding: '1rem 1rem 1rem 0rem' }}> <Divider /> </Box>
        </Box>
      }
      <Box pt={1} pb={2}>
        <Typography variant='subtitle2'>Patient profile</Typography>
        <Grid container spacing={2} justifyContent="space-between" className={classes.patientProfileContainer}>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Username
            </Typography>
            <Typography variant="body2">
              {checkCondition(values.username !== '', values.username, '-')}
            </Typography>
          </Grid>
          {!Helper.isAIDA() &&
            <Grid item xs={5.9}>
              <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>Clinic Patient Id</Typography>
              <TextField
                fullWidth={true}
                variant="standard"
                value={values?.clinic_patient_id || ''}
                name="clinic_patient_id"
                onChange={handleInputChange}
              />
            </Grid>
          }
          {/* Healthy Heart only — the payer-side care manager comes from that
              referral sheet and means nothing for other cohorts. Shown even when
              empty so staff can fill one in; the referral import seeds blanks
              only, so an edit here is not overwritten by a later re-upload. */}
          {values?.hospital_name === 'healthyheart' &&
            <Grid item xs={5.9}>
              <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
                Referring care manager
              </Typography>
              <TextField
                fullWidth={true}
                variant="standard"
                value={values?.referring_caremgr_id || ''}
                name="referring_caremgr_id"
                onChange={handleInputChange}
              />
            </Grid>
          }
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>MRN</Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.MRN}
              name="MRN"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Name prefix
            </Typography>
            <Select
              fullWidth={true}
              variant="standard"
              displayEmpty
              className={classes.cardLabNotes}
              inputProps={{ 'aria-label': 'With label' }}
              value={values.name_prefix}
              name="name_prefix"
              onChange={handleInputChange}
            >
              {prefix.map((r) => (
                <MenuItem className={classes.desc} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Given name
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              placeholder="enter given name"
              value={values.given_name}
              name="given_name"
              onChange={handleInputChange}
            />
            {checkCondition(nameError,
              <Typography variant="body2" className={classes.errorText}>
                Given name can not be cleared
              </Typography>
            )}
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Middle name
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.middle_name}
              name="middle_name"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Family name
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.family_name}
              name="family_name"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Sex/Gender
            </Typography>
            <Select
              fullWidth={true}
              variant="standard"
              displayEmpty
              className={classes.cardLabNotes}
              inputProps={{ 'aria-label': 'With label' }}
              value={values.c_sex}
              name="c_sex"
              onChange={handleInputChange}
            >
              <MenuItem value="Male" className={classes.desc}>Male</MenuItem>
              <MenuItem value="Female" className={classes.desc}>Female</MenuItem>
            </Select>
            {validateInput.includes('c_sex') &&
              <FormHelperText>
                <Typography style={{ fontSize: '0.75rem' }} component="span" color="error">Please enter gender.</Typography>
              </FormHelperText>
            }
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Email
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              name="email"
              value={values.email}
              onChange={handleInputChange}
              error={validateInput.includes('email')}
              helperText={
                validateInput.includes('email')
                  ? 'Please enter valid email.'
                  : ''
              }
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Birthdate
            </Typography>
            <DatePickerComponent
              name="birthdate"
              variant="standard"
              type="date"
              cssClass={classes.cardLabNotes}
              inputPropCss={classes.cardLabDate}
              value={values.birthdate}
              onCalendarChange={handleInputChange}
            />
            {validateInput.includes('birthdate') &&
              <FormHelperText>
                <Typography style={{ fontSize: '0.75rem' }} component="span" color="error">Please enter birthdate.</Typography>
              </FormHelperText>
            }
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Phone
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.phone}
              name="phone"
              onChange={handleInputChange}
              error={validateInput.includes('phone')}
              helperText={
                validateInput.includes('phone')
                  ? 'Please enter valid phone no.'
                  : ''
              }
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Email active
            </Typography>
            <Select
              fullWidth={true}
              variant="standard"
              displayEmpty
              className={classes.selectEmpty}
              inputProps={{ 'aria-label': 'With label' }}
              value={values.email_is_active === true ? 'Yes' : values.email_is_active === false ? 'No' : ''}
              name="email_is_active"
              onChange={(e: any) => {
                const val = e.target.value === 'Yes' ? true : e.target.value === 'No' ? false : null
                handleInputChange({ target: { name: 'email_is_active', value: val } })
              }}
            >
              <MenuItem className={classes.desc} value="">Not asked</MenuItem>
              <MenuItem className={classes.desc} value="Yes">Yes</MenuItem>
              <MenuItem className={classes.desc} value="No">No</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Proxy email
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.proxy_email}
              name="proxy_email"
              placeholder="Caregiver/family email"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Proxy email name
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.proxy_email_name}
              name="proxy_email_name"
              placeholder="First name"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Proxy email relationship
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.proxy_email_relationship}
              name="proxy_email_relationship"
              placeholder="e.g. spouse, child, caregiver"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Proxy phone
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.proxy_phone}
              name="proxy_phone"
              placeholder="Caregiver/family phone"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Proxy phone name
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.proxy_phone_name}
              name="proxy_phone_name"
              placeholder="First name"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Proxy phone relationship
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.proxy_phone_relationship}
              name="proxy_phone_relationship"
              placeholder="e.g. spouse, child, caregiver"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              SMS consent
            </Typography>
            <Select
              fullWidth={true}
              variant="standard"
              value={values.sms_opted_in === true ? "Yes" : values.sms_opted_in === false ? "No" : ""}
              name="sms_opted_in"
              onChange={(e) => {
                const val = e.target.value;
                handleInputChange({
                  target: {
                    name: "sms_opted_in",
                    value: val === "Yes" ? true : val === "No" ? false : null,
                  },
                } as any);
                if (val === "Yes" || val === "No") {
                  handleInputChange({
                    target: {
                      name: "sms_opted_in_date",
                      value: Math.floor(Date.now() / 1000),
                    },
                  } as any);
                }
              }}
              displayEmpty
            >
              <MenuItem value="">Not asked</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              SMS intro sent
            </Typography>
            <Select
              fullWidth={true}
              variant="standard"
              value={values.sms_intro_sent === true ? "Yes" : values.sms_intro_sent === false ? "No" : ""}
              name="sms_intro_sent"
              onChange={(e) => {
                const val = e.target.value;
                handleInputChange({
                  target: {
                    name: "sms_intro_sent",
                    value: val === "Yes" ? true : val === "No" ? false : null,
                  },
                } as any);
                if (val === "Yes") {
                  handleInputChange({
                    target: {
                      name: "sms_intro_sent_date",
                      value: Math.floor(Date.now() / 1000),
                    },
                  } as any);
                }
              }}
              displayEmpty
            >
              <MenuItem value="">Not set</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ padding: '0.5rem 0rem' }}><Divider /></Box>
            <Typography variant='subtitle2'>Contact preferences</Typography>
            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
              All times in Eastern. CT = -1hr, MT = -2hr, PT = -3hr
            </Typography>
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Patient timezone
            </Typography>
            <Select
              fullWidth={true}
              variant="standard"
              displayEmpty
              className={classes.selectEmpty}
              value={values.preferred_contact_timezone || ''}
              name="preferred_contact_timezone"
              onChange={handleInputChange}
            >
              <MenuItem value="">Not set</MenuItem>
              {['Eastern', 'Central', 'Mountain', 'Pacific', 'Alaska', 'Hawaii'].map((tz) => (
                <MenuItem key={tz} className={classes.desc} value={tz}>{tz}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Preferred contact time
            </Typography>
            <Select
              fullWidth={true}
              variant="standard"
              displayEmpty
              multiple
              className={classes.selectEmpty}
              value={values.preferred_contact_time_blocks ? values.preferred_contact_time_blocks.split(',').map((s: string) => s.trim()).filter(Boolean) : []}
              renderValue={(selected: string[]) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value: string) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              onChange={(e: any) => {
                const val = e.target.value as string[]
                handleInputChange({ target: { name: 'preferred_contact_time_blocks', value: val.join(', ') } })
              }}
            >
              {['Early Morning (7-9 AM)', 'Morning (9-12 PM)', 'Afternoon (12-3 PM)', 'Late Afternoon (3-5 PM)', 'Evening (5-7 PM)', 'Any'].map((block) => (
                <MenuItem key={block} className={classes.desc} value={block}>{block}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Preferred contact days
            </Typography>
            <Select
              fullWidth={true}
              variant="standard"
              displayEmpty
              multiple
              className={classes.selectEmpty}
              value={values.preferred_contact_days ? values.preferred_contact_days.split(',').map((s: string) => s.trim()).filter(Boolean) : []}
              renderValue={(selected: string[]) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value: string) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              onChange={(e: any) => {
                const val = e.target.value as string[]
                handleInputChange({ target: { name: 'preferred_contact_days', value: val.join(', ') } })
              }}
            >
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Weekdays', 'Any'].map((day) => (
                <MenuItem key={day} className={classes.desc} value={day}>{day}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ padding: '0.5rem 0rem' }}><Divider /></Box>
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Communication language
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.communication_language}
              name="communication_language"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Marital status
            </Typography>
            <Select
              fullWidth={true}
              variant="standard"
              displayEmpty
              className={classes.selectEmpty}
              inputProps={{ 'aria-label': 'With label' }}
              value={values.marital_status}
              name="marital_status"
              onChange={handleInputChange}
            >
              {maritalStatus.map((r) => (
                <MenuItem className={classes.desc} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              City
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.city}
              name="city"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              State
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.state || ''}
              name="state"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Country
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.country}
              name="country"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Address1
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.address_line_1}
              name="address_line_1"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Address2
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.address_line_2}
              name="address_line_2"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Zip code
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.zip_code}
              name="zip_code"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Ethnicity
            </Typography>
            <Select
              fullWidth={true}
              variant="standard"
              disabled
              displayEmpty
              className={classes.selectEmpty}
              inputProps={{ 'aria-label': 'With label' }}
              value={values.ethnicity}
              name="ethnicity"
              onChange={handleInputChange}
            >
              <MenuItem className={classes.desc} value="Male">ethnicity1</MenuItem>
              <MenuItem className={classes.desc} value="Female">ethnicity2</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Responsible party first name
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.responsible_party_first_name}
              name="responsible_party_first_name"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
              Responsible party last name
            </Typography>
            <TextField
              fullWidth={true}
              variant="standard"
              value={values.responsible_party_last_name}
              name="responsible_party_last_name"
              onChange={handleInputChange}
            />
          </Grid>
          {!Helper.isAIDA() &&
            <Grid item xs={5.9}>
              <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>Hospital</Typography>
              <Select
                fullWidth={true}
                variant="standard"
                displayEmpty
                className={classes.selectEmpty}
                inputProps={{ 'aria-label': 'With label' }}
                value={values.hospital_name}
                name="hospital_name"
                onChange={handleInputChange}
              >
                {Object.keys(hospitals)?.sort()?.map((key: any) => (
                  <MenuItem className={classes.desc} value={key}>{hospitals[key]}</MenuItem>
                ))}
              </Select>
            </Grid>
          }
          <Grid item xs={5.9}>
            <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>Assigned users</Typography>
            <Autocomplete
              clearIcon={true}
              sx={{
                "& .MuiOutlinedInput-root": {
                  padding: '0.1rem 0.1rem 0.1rem 0rem !important'
                }
              }}
              options={[]}
              freeSolo
              multiple
              value={values?.assigned_users || []}
              renderTags={(value: any, props: any) =>
                value?.map((option: any, index: any) => <Chip label={option} {...props({ index })} />)
              }
              renderInput={(params: any) => <TextField
                className={classes.desc}
                variant='standard'
                // placeholder='Type & press enter key to add'
                {...params}
              />
              }
              onChange={(e: any, val: any) => handleInputChange('assigned_users', val)}
              onKeyUp={(e: any) => {
                if (e.target.value !== '')
                  handleInputChange('update_assigned_users', [...values?.assigned_users, e.target.value])
              }}
            />
            <Typography sx={{ fontSize: '0.75rem' }} component="span" color="grey">
              <i>Type & press enter key to add.</i>
            </Typography>
            {validateInput.includes('assigned_users') &&
              <FormHelperText>
                <Typography sx={{ fontSize: '0.75rem' }} component="span" color="error">Please enter valid email.</Typography>
              </FormHelperText>
            }
          </Grid>
        </Grid>
      </Box>
      {/* Atman Elation MRN - only shown for LifeMD patients */}
      {values.elation_source === 'lifemd' &&
        <Box>
          <Box sx={{ padding: '1rem 1rem 1rem 0rem' }}> <Divider /> </Box>
          <Typography variant='subtitle2'>AI Extraction Settings</Typography>
          <Box pt={1} pb={1}>
            <Grid container justifyContent="space-between" spacing={2}>
              <Grid item xs={5.9}>
                <Typography variant="subtitle2" color="textSecondary" className={classes.subHeaderTitle}>
                  Atman Elation MRN
                </Typography>
                <TextField
                  variant="standard"
                  fullWidth={true}
                  value={values.atman_elation_mrn || ''}
                  name="atman_elation_mrn"
                  onChange={handleInputChange}
                  placeholder="Optional MRN for AI extraction"
                />
                <Typography sx={{ fontSize: '0.75rem' }} component="span" color="grey">
                  <i>If set, AI extraction will use this MRN from Atman's Elation.</i>
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      }
    </Box>
  )
}

export default withStyles(stylesheet)(PatientProfileDetailsComponent)
